import React, { createContext, Dispatch, MutableRefObject, Reducer, useContext, useEffect, useReducer, useRef } from 'react';

export const MenuContext = createContext<[TMenuState, Dispatch<TMenuAction>] | null>(null);
MenuContext.displayName = 'MenuContext';

const initialIsOpen = false; // needs to start as false to prevent multiple elements being opened at the same time

type emptyFn = () => void;

export function useMenuContext(): [TMenuState, Dispatch<TMenuAction> | emptyFn] {
  const value = useContext(MenuContext);
  if (value) {
    return value;
  }
  console.warn('Missing MenuContext provider');
  // this is necessary to prevent errors when using this outside of the context
  // eslint-disable-next-line @typescript-eslint/no-empty-function
  return [
    [],
    (): void => {
      // This is intentional
    },
  ];
}

export function useMenuDispatcher() {
  return useMenuContext()[1];
}

export function useMenuState() {
  return useMenuContext()[0];
}

export function useMenuItem(menuState: TMenuState, menuDispatcher: Dispatch<TMenuAction>): [boolean, TSetOpen] {
  const elementRef = useRef(null); // value that never changes in different renderings
  const isOpen = menuState.find((element) => element.ref === elementRef)?.isOpen ?? initialIsOpen;

  const setOpen = (newIsOpen = true) => {
    menuDispatcher({ type: newIsOpen ? 'openElement' : 'closeElement', ref: elementRef });
  };

  useEffect(() => {
    menuDispatcher({ type: 'addElement', ref: elementRef, initialIsOpen });
    return () => {
      menuDispatcher({ type: 'removeElement', ref: elementRef });
    };
  }, []); // eslint-disable-line

  return [isOpen, setOpen];
}

export type TSetOpen = (newIsOpen: boolean) => void;

export function useMenuItemWithContext(): [boolean, TSetOpen] {
  const [menuState, menuDispatcher] = useMenuContext();
  return useMenuItem(menuState, menuDispatcher);
}

export type TMenuState = { ref: MutableRefObject<null>; isOpen: boolean }[];
export type TMenuAction =
  | {
      type: 'addElement';
      initialIsOpen: boolean;
      ref: MutableRefObject<null>;
    }
  | {
      type: 'openElement' | 'removeElement' | 'closeElement';
      ref: MutableRefObject<null>;
    }
  | {
      type: 'closeAll';
    };
type TMenuReducer = Reducer<TMenuState, TMenuAction>;

const initialState: TMenuState = [];

const menuReducer: TMenuReducer = (state, action) => {
  switch (action.type) {
    case 'addElement':
      return [...state, { ref: action.ref, isOpen: action.initialIsOpen }];
    case 'openElement':
      return state.map((element) => {
        element.isOpen = element.ref === action.ref;
        return element;
      });
    case 'removeElement':
      return state.filter((element) => element.ref !== action.ref);
    case 'closeElement': // same as closeAll, there should never be more than one open
    case 'closeAll':
      return state.map((element) => {
        element.isOpen = false;
        return element;
      });
    default:
      return state;
  }
};

export function useMenuReducer() {
  return useReducer<TMenuReducer>(menuReducer, initialState);
}

export function MenuProvider(props: any) {
  const [menuState, menuDispatch] = useMenuReducer();

  return <MenuContext.Provider {...props} value={[menuState, menuDispatch]} />;
}
